import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TIMING_NUTRIENTS } from '@/lib/nutrientDistribution';
import { NUTRIENT_TIMING_MAX } from '@/hooks/useNutrientTimingPrefs';
import { cn } from '@/lib/utils';

/**
 * Compact in-card multi-select for the Nutrient-timing chart — pick up to 3
 * nutrients. Writes through the same `useNutrientTimingPrefs` value the Profile
 * editor uses, so changes made here persist and show up there too.
 */
export const NutrientTimingSelect = ({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) => {
  const { t } = useTranslation();

  const toggle = (key: string) => {
    if (selected.includes(key)) {
      onChange(selected.filter((k) => k !== key));
    } else if (selected.length < NUTRIENT_TIMING_MAX) {
      onChange([...selected, key]);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5 px-2.5 text-xs">
          {t('Nutrients')}
          <span className="text-muted-foreground">
            {t('{{count}}/{{max}}', { count: selected.length, max: NUTRIENT_TIMING_MAX })}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        {TIMING_NUTRIENTS.map((n) => {
          const active = selected.includes(n.key);
          const atMax = !active && selected.length >= NUTRIENT_TIMING_MAX;
          return (
            <button
              key={n.key}
              type="button"
              onClick={() => toggle(n.key)}
              aria-pressed={active}
              disabled={atMax}
              className={cn(
                'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors',
                atMax ? 'cursor-not-allowed opacity-40' : 'hover:bg-accent',
              )}
            >
              <span aria-hidden className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: n.colour }} />
              <span className="min-w-0 flex-1 truncate text-left">{t(n.label)}</span>
              {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
};
